import { FileText, Receipt } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { formatCurrency, formatDateTimeValue } from '@/lib/utils';

import { TurnoMetodosPagoDialog } from '@/features/reportes/components/turno-metodos-pago-dialog';
import { usePermisosLiquidacion } from '@/features/reportes/reportes.api';

import { TablaDelTurno } from '../components/tabla-turno';
import type { ColumnaDelTurno } from '../components/tabla-turno';
import { turnoApi, useDocumentosDelTurno, useTurnoActual } from '../turno.api';
import type { DocumentoDelTurno } from '../turno.types';

/**
 * Documentos del turno (`/turno/documentos`).
 *
 * Puerto de la pantalla *Documentos* del grifero (`Turno::documents`): las boletas y
 * facturas del turno abierto, con la corrección de cobros del turno (el diálogo que
 * ya usa el reporte de turnos). El PDF de cada comprobante se genera en la API; el
 * panel anterior lo imprimía con QZ Tray en el equipo del operador.
 */
export function TurnoDocumentosPage() {
  useDocumentTitle('Turno · Documentos');

  const turno = useTurnoActual();
  const permisos = usePermisosLiquidacion();

  const [pagina, setPagina] = useState(1);
  const [buscar, setBuscar] = useState('');
  const [cobros, setCobros] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const listado = useDocumentosDelTurno({ page: pagina, pageSize: 25, buscar });
  const filas = listado.data?.items ?? [];

  const verPdf = async (fila: DocumentoDelTurno) => {
    setAviso(null);

    try {
      const nombre = `${fila.tipo_comprobante?.toLowerCase() ?? 'comprobante'}-${fila.comprobante_serie ?? ''}${fila.comprobante_numero ?? ''}`;
      await abrirPdfAutenticado(turnoApi.ticketDocumento(fila.id), { nombre });
    } catch (error) {
      setAviso(error instanceof Error ? error.message : 'No se pudo abrir el comprobante.');
    }
  };

  const columnas: ColumnaDelTurno<DocumentoDelTurno>[] = [
    {
      encabezado: 'Fecha',
      celda: (fila) => formatDateTimeValue(fila.created_at),
    },
    {
      encabezado: 'Cliente',
      celda: (fila) => (
        <span className="block">
          <span className="block text-ink-800">{fila.cliente_nombre ?? '—'}</span>
          <span className="text-xs text-ink-400">{fila.cliente_numero ?? ''}</span>
        </span>
      ),
    },
    { encabezado: 'Placa', celda: (fila) => fila.cliente_placa ?? '—' },
    {
      encabezado: 'Comprobante',
      celda: (fila) => (
        <span className="flex items-center gap-2">
          <Badge variant={fila.tipo_comprobante === 'FACTURA' ? 'brand' : 'neutral'}>
            {fila.tipo_comprobante ?? '—'}
          </Badge>
          <span className="tabular-nums">
            {fila.comprobante_serie ?? ''}-{fila.comprobante_numero ?? ''}
          </span>
        </span>
      ),
    },
    {
      encabezado: 'Descuento',
      clase: 'text-right',
      celda: (fila) => formatCurrency(fila.descuento ?? 0),
    },
    {
      encabezado: 'Monto',
      clase: 'text-right',
      celda: (fila) => (
        <span className="font-medium text-ink-800">{formatCurrency(fila.monto_pagado ?? 0)}</span>
      ),
    },
    {
      encabezado: 'M. Pago',
      celda: (fila) => {
        const partes: string[] = [];

        if ((fila.monto_pago_primario ?? 0) > 0) {
          partes.push(`${fila.metodo_pago ?? ''} ${formatCurrency(fila.monto_pago_primario ?? 0)}`);
        }

        if ((fila.monto_pago_secundario ?? 0) > 0) {
          partes.push(`+ ${formatCurrency(fila.monto_pago_secundario ?? 0)}`);
        }

        return <span className="text-xs text-ink-600">{partes.join(' ') || '—'}</span>;
      },
    },
    {
      encabezado: 'Acciones',
      celda: (fila) => (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<FileText className="size-4" aria-hidden="true" />}
          onClick={() => void verPdf(fila)}
        >
          PDF
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Receipt className="size-6 text-brand-500" aria-hidden="true" />
          Documentos del turno
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">
          Boletas y facturas emitidas en tu turno. Si una venta quedó con el cobro equivocado, se
          corrige desde los métodos de pago del turno.
        </p>
      </header>

      {aviso ? (
        <Alert variant="error" title="No se pudo abrir el comprobante">
          {aviso}
        </Alert>
      ) : null}

      <TablaDelTurno
        titulo="Comprobantes del turno"
        descripcion={
          turno.data?.abierto
            ? `Turno #${turno.data.turno?.id ?? ''} abierto.`
            : 'Cuando abras tu turno verás aquí sus comprobantes.'
        }
        columnas={columnas}
        filas={filas}
        total={listado.data?.total ?? 0}
        pagina={pagina}
        tamanioPagina={25}
        buscar={buscar}
        onBuscar={(valor) => {
          setBuscar(valor);
          setPagina(1);
        }}
        onPagina={setPagina}
        cargando={listado.isLoading}
        vacio="Todavía no hay comprobantes en este turno."
        sinTurno={turno.data ? !turno.data.abierto : false}
        avisoSinTurno="sus comprobantes"
        acciones={
          turno.data?.turno ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Receipt className="size-4" aria-hidden="true" />}
              onClick={() => setCobros(true)}
            >
              Métodos de pago del turno
            </Button>
          ) : null
        }
      />

      <TurnoMetodosPagoDialog
        turno={cobros ? (turno.data?.turno ?? null) : null}
        permisos={permisos.data}
        onClose={() => setCobros(false)}
        onCambio={async () => {
          await Promise.all([turno.refetch(), listado.refetch()]);
        }}
      />
    </div>
  );
}
